import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { Grid, Button } from '@mui/material';
import React, { useEffect, useState, useRef, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import { useApolloClient } from '@apollo/client';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RootContext from '../../../util/root.context';
import useLocalStorage from '../../hooks/useLocalStorage';
import EirAddonsCard from '../eir-addons/EirAddonsCard';
import EirInformation from '../eir-information/EirInformation';
import { getMbbDevices, addMbbDevices, removeMbbDevices } from './Mbb.util';
import { formatMbbDevicesData } from '../../../util/Format.util';
import { displayCart } from '../eir-cart/cart.util';
import styles from './style';
import { useToggle } from '../../hooks/useToggle';
import { getCookie } from '../../hooks/useCookie';
import ApiFailureModal from '../eir-error-modal/ApiFailureModal';

const { publicRuntimeConfig } = getConfig();

const MbbDevicesContainer = ({
  showMbbDevice,
  OfferCode,
  mbbDevicesCards,
  setMbbDevicesCards,
  selectedAddOnCode,
}) => {
  const [loading, setLoading] = useState(true);
  const localStore = useLocalStorage();
  const { state: rootState, dispatch } = useContext(RootContext);
  const [showLoader, setShowLoader] = useState({ code: ' ', value: false });
  const [hasMbbDevices, setHasMbbDevices] = useState(false);
  const openAPIFailureModal = useToggle(false);
  const [openError, setOpenError] = useState(false);
  const [nextButtonStatus, setNextButtonStatus] = useState(true);
  const graphql = useApolloClient();
  const scrollRef = useRef(null);
  const theme = useTheme();
  const router = useRouter();

  const nextStep = () => {
    router.push(`${publicRuntimeConfig.NEXT_PUBLIC_PATH}/summary`);
  };
  const handleOk = () => {
    openAPIFailureModal.disable();
  };
  const onLoadMbbDevices = async () => {
    try {
      const data = await getMbbDevices(graphql, rootState.cart);
      if (data != null && !data.error) {
        if (data?.dongles?.length > 0) {
          setMbbDevicesCards(formatMbbDevicesData(data.dongles));
          setHasMbbDevices(true);
          if (selectedAddOnCode) {
            const tempCards = [...formatMbbDevicesData(data.dongles)];
            const objIndex = tempCards?.findIndex(card => card?.code === selectedAddOnCode);
            tempCards[objIndex].AddonsSelected = true;
            setMbbDevicesCards(tempCards);
          }
        } else {
          setHasMbbDevices(false);
        }
      } else {
        if (!getCookie('token-missing') && !getCookie('found-403')) setOpenError(true);
        if (!getCookie('token-missing') && !getCookie('found-403')) openAPIFailureModal.enable();
      }
    } catch (error) {
      setShowLoader('');
    }
  };

  const addAddon = async (serviceGroup, catalogCode) => {
    setShowLoader({ ...showLoader, code: catalogCode });
    const data = await addMbbDevices(graphql, catalogCode, rootState.cart);
    if (data != null && !data.error) {
      setNextButtonStatus(false);
      dispatch({ type: 'SET_CART', payload: data });
      displayCart({
        dispatch,
        localStorage: localStore,
        graphql,
        cart: data,
        showNextButton: false,
      });
      // changing the cta to Added
      if (serviceGroup === 'MBB') {
        const tempCards = [...mbbDevicesCards];
        const objIndex = mbbDevicesCards.findIndex(card => card.code === catalogCode);
        tempCards[objIndex].AddonsSelected = true;
        setMbbDevicesCards(tempCards);
        setShowLoader({ ...showLoader, code: ' ' });
      }
    } else {
      setShowLoader({ ...showLoader, code: ' ' });
    }
  };
  const removeAddOn = async (serviceGroup, catalogCode) => {
    setShowLoader({ ...showLoader, code: catalogCode });
    const data = await removeMbbDevices(graphql, catalogCode, rootState.cart);
    if (data != null && !data.error) {
      setNextButtonStatus(true);
      dispatch({ type: 'SET_CART', payload: data });
      displayCart({
        dispatch,
        localStorage: localStore,
        graphql,
        cart: data,
        showNextButton: false,
      });
      // changing the cta to Add
      if (serviceGroup === 'MBB') {
        const tempCards = [...mbbDevicesCards];
        const objIndex = mbbDevicesCards.findIndex(card => card.code === catalogCode);
        tempCards[objIndex].AddonsSelected = false;
        setMbbDevicesCards(tempCards);
        setShowLoader({ ...showLoader, code: ' ' });
      }
    } else {
      setShowLoader({ ...showLoader, code: ' ' });
    }
  };

  useEffect(() => {
    if (mbbDevicesCards?.length > 0) {
      mbbDevicesCards?.map(card => setNextButtonStatus(!card.AddonsSelected));
    }
    setLoading(false);
  }, [mbbDevicesCards]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollIntoView();
  }, [OfferCode]);

  useEffect(() => {
    if (showMbbDevice) {
      onLoadMbbDevices();
    }
  }, [OfferCode]);

  return (
    <Grid
      container
      maxWidth={1010}
      justifyContent={mbbDevicesCards?.length <= 1 && 'center'}
      sx={styles(theme).mbbDeviceContainer}
      ref={scrollRef}
    >
      {loading && !hasMbbDevices ? (
        <EirAddonsCard card={[]} addAddon={() => {}} removeAddOn={() => {}} loading={loading} />
      ) : (
        <>
          <EirInformation
            title='Choose your perfect device:'
            description=' '
            textAlign='center'
            isMbb
          />

          {mbbDevicesCards?.map((card, index) => (
            <EirAddonsCard
              card={card}
              key={index}
              addAddon={addAddon}
              removeAddOn={removeAddOn}
              showLoader={showLoader}
              loading={loading}
              mbbDescription='true'
              mbbDevices='true'
            />
          ))}

          <Grid item xs={12} sx={styles(theme).submitCTAContainerMbbDevices}>
            <Button
              variant='buttonMedium'
              disabled={nextButtonStatus}
              sx={
                !nextButtonStatus
                  ? styles(theme).submitCTAMbbDevices
                  : styles(theme).submitCTAMbbDevicesDisabled
              }
              onClick={() => nextStep()}
              aria-label='Proceed to review your order'
            >
              Next
              <ArrowForwardIcon sx={{ ml: 1 }} />
            </Button>
          </Grid>
        </>
      )}
      {openError && (
        <ApiFailureModal openAPIFailureModal={openAPIFailureModal} handleOk={handleOk} />
      )}
    </Grid>
  );
};

export default MbbDevicesContainer;
